'use client';

import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PortableTextBlock } from 'sanity';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GROQButton } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';
import { motion, useScroll } from 'framer-motion';
import logo from '@/public/logo-black.svg';
import arrow from '@/public/comp_affiliate-v2/arrows.svg';
import Countdown from '@/components/global/countdown/Countdown';
import { GROQImage } from '@/sanity/lib/types';
import shadow from '@/public/comp_affiliate-v2/Shadow.png';
import shadowM from '@/public/comp_affiliate-v2/ShadowM.png';

export default function LastCTACompetitionSection({
  chip,
  text,
  cta,
  className,
  textClassName,
  ctaClassName,
  partnerName,
  partnerImage,
  toggleCountdown,
  countdown,
}: {
  chip?: string;
  text: PortableTextBlock[];
  cta: GROQButton;
  className?: string;
  textClassName?: string;
  ctaClassName?: string;
  partnerName?: string;
  partnerImage?: GROQImage;
  toggleCountdown?: boolean;
  countdown?: string;
}) {
  const parallaxRef = useRef(null);
  useScroll({ target: parallaxRef, offset: ['start end', 'end start'] });

  return (
    <section
      ref={parallaxRef}
      className={cn(
        'relative overflow-clip px-[2rem] lg:px-[5rem] lg:pb-[15rem] pb-[8rem]',
        className,
      )}
    >
      <Image
        src={shadow}
        alt=""
        className="absolute inset-0 !w-full !h-full object-cover pointer-events-none hidden lg:block mix-blend-hard-light"
      />
      <div className="absolute top-0 left-0 h-[15rem] w-full bg-gradient-to-b from-white to-transparent" />
      <div className="absolute bottom-0 left-0 h-[15rem] w-full bg-gradient-to-t from-white to-transparent" />
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[3.5rem] lg:pt-[13rem] border border-black/5 lg:border-none pb-[6rem] lg:pb-0">
        <Image
          src={shadowM}
          alt=""
          className="absolute inset-0 !w-full !h-full pointer-events-none lg:hidden"
        />
        <div className="container px-[3rem] flex flex-col items-start lg:items-center relative z-10">
          {partnerName && (
            <div className="flex gap-[2rem] items-center mb-[2.62rem]">
              <Image src={logo} alt="" className="w-[16.25rem]" />
              <Image
                src={arrow}
                alt=""
                className="flex-shrink-0 !w-[9.5rem] lg:!w-[10.875rem]"
              />
              <div className="flex items-center gap-[1rem]">
                {partnerImage && (
                  <div className="rounded-full border border-[#DD002A] p-[0.25rem] relative">
                    <Image
                      src={partnerImage}
                      alt=""
                      className="flex-shrink-0 !w-[5.5rem] lg:!w-[6.5rem] aspect-square object-cover object-top rounded-full"
                    />
                    <div className="absolute bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 bg-[#DC002A] rounded-[0.285rem] h-[2rem] w-[4.8rem] text-center flex items-center">
                      <span className="text-white leading-none text-[1.72075rem] w-full h-full translate-y-[0.15rem] tracking-[0.01rem]">
                        LIVE
                      </span>
                    </div>
                  </div>
                )}
                <p className="text-[#103037] lg:text-[#5BA66E] italic text-[1.75rem] lg:text-[2rem] font-[500] leading-[116%] m-0">
                  {partnerName}
                </p>
              </div>
            </div>
          )}
          {chip && (
            <p className="text-black/70 font-medium mb-[1.5rem] lg:mb-[2rem] text-[1.75rem] lg:text-[2rem]">
              {chip}
            </p>
          )}
          {text && (
            <div
              className={cn(
                'richtext richtext-start lg:richtext-center w-[min(80rem,100%)] prose-h2:w-[40rem] sm:prose-h2:w-auto prose-h2:text-[5rem] lg:prose-h2:text-[8rem]',
                textClassName,
              )}
            >
              <PortableText value={text} components={richTextComponent} />
            </div>
          )}
          {cta && (
            <CTAButton className={cn('mt-[3rem]', ctaClassName)} data={cta} />
          )}
          {toggleCountdown && countdown && (
            <div className="mt-[2.5rem] w-[min(70.875rem,100%)] mx-auto rounded-[1.5rem] lg:rounded-[2.25rem] backdrop-blur-[5.375rem] bg-[#0F2D33] shadow-[0_-1.072px_41.904px_0_#8DCDC0_inset] [&_.countdownUnitItem]:border-white/10">
              <Countdown dateTimeString={countdown ?? ''} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
